/**
 * "Sent with Bundle · mail, chat and calls in one app": the small grey line under my signature on mail I write in
 * Bundle (Spark's "Sent with Spark", Outlook's "Get Outlook for Android"). The composer shows it (✕ leaves it off one
 * message) and asks for it when it sends; the server adds it then, below my signature and above the quoted original,
 * in the HTML and the plain-text part (apps/api mail/compose). Mail nobody wrote in a composer (vacation replies, rule
 * forwards, calendar answers, apps and agents) never gets it. docs/features/mail.md "Sent with Bundle".
 */
import { format } from "@bundle/i18n";
import { EMAIL_TOKENS } from "../generated/emailTokens.ts";
import { joinComposer, splitComposer } from "./mail.ts";

/** What the recipient sees, in the sender's app language, and where it links. */
export type SentWithLine = { text: string; url: string };

const URLS = { en: "https://bundle.direct/?ref=mail", "pt-BR": "https://bundle.direct/pt-br/?ref=mail" } as const;

/**
 * The line in the sender's app language (Member.locale): Portuguese → pt-BR, anything else English. The text is
 * packages/i18n's `mail.sentWith.line`, so the composer's preview and the server's message are the same string.
 */
export function sentWithLine(locale: string | undefined): SentWithLine {
  const l = locale?.toLowerCase().startsWith("pt") ? "pt-BR" : "en";
  return { text: format(l, "mail.sentWith.line"), url: URLS[l] };
}

/** My setting for a mailbox: on unless I turned it off (shared mailboxes too: the user chose reach). */
export function sentWithOn(mailbox: { sentWith?: boolean | undefined } | undefined): boolean {
  return mailbox !== undefined && mailbox.sentWith !== false;
}

/**
 * Whether the composer shows the line (and the send asks for it): the workspace allows it (Workspace.mailSentWith,
 * absent = yes), it's on for this mailbox and I didn't ✕ it on this message.
 */
export function wantsSentWith(p: {
  workspaceAllows: boolean | undefined;
  mailbox: { sentWith?: boolean | undefined } | undefined;
  removed: boolean;
}): boolean {
  return p.workspaceAllows !== false && sentWithOn(p.mailbox) && !p.removed;
}

/** The plain-text part's line: "Sent with Bundle · … <https://…>" (angle brackets keep the URL whole for linkifiers). */
export function sentWithText(line: SentWithLine): string {
  return `${line.text} <${line.url}>`;
}

const esc = (s: string) => s.replace(/[&<>"']/g, (c) => ({ "&": "&amp;", "<": "&lt;", ">": "&gt;", '"': "&quot;", "'": "&#39;" })[c]!);

/** The HTML part's line: a small grey paragraph (`text.tertiary`), inline styles only (Gmail and Outlook drop <style> rules). */
export function sentWithHtml(line: SentWithLine): string {
  const grey = EMAIL_TOKENS.textTertiary;
  return `<p class="bundle-sent-with" style="margin:20px 0 0;font-size:12px;line-height:16px;color:${grey}"><a href="${esc(line.url)}" style="color:${grey};text-decoration:none">${esc(line.text)}</a></p>`;
}

/**
 * Where the line goes in the composer's text: `head` is what I wrote and my signature ("-- " block), `quote` the quoted
 * original ("" when there is none). The message as sent is head, the line, then quote.
 */
export function sentWithSplit(text: string): { head: string; quote: string } {
  const parts = splitComposer(text);
  return { head: joinComposer({ ...parts, quote: "" }), quote: parts.quote };
}
